import { Assets } from '@air/api';
import { getExtData } from '@air/api/shared-utils/utils/exts';
import { type ClipType, type WorkspaceListResponse } from '@air/api/types';
import { type QueryClient, skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { type MutableRefObject, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import {
  centralizedClipKeywordsSelector,
  centralizedClipSelector,
  centralizedClipSummarySelector,
} from '~/store/centralizedClip/selectors';
import { type CentralizedClip } from '~/store/centralizedClip/types';
import { callAirApi } from '~/swr-hooks/callAirApi';
import { useStorage } from '~/swr-hooks/subscriptions/useStorage';
import { getClipWithoutWorkspaceIdKey } from '~/swr-hooks/useGetClipWithNoWorkspaceId';
import { useCanViewPrivateAddOn } from '~/swr-hooks/workspaces/addOns/useCanViewPrivateAddOn';

type IntelligenceStatusType = 'error' | 'processing' | 'complete' | 'none' | 'pending' | 'dispatched' | 'empty';

type IntelligenceStatus = {
  status: IntelligenceStatusType;
  updatedAt?: string;
};

// Constants for status groups
const IN_PROGRESS_STATUSES: IntelligenceStatusType[] = ['processing', 'pending', 'dispatched'];
const COMPLETE_STATUSES: IntelligenceStatusType[] = ['complete', 'error', 'empty'];
const ERROR_STATUSES: IntelligenceStatusType[] = ['error', 'empty'];

const getIntelligenceType = (clipType: ClipType, ext?: string): 'video' | 'image' | null => {
  if (clipType === 'nonMedia' || !ext) return null;

  const extData = getExtData(ext);

  // Use backend's actual classification
  if (extData.type === 'video') return 'video';

  // Treat all visual content (including documents) as images
  // This includes PDFs, Word docs, PowerPoints that are converted to images
  if (['photo', 'animated', 'livePhoto'].includes(extData.type)) {
    return 'image';
  }

  return null;
};

const createIntelligenceStatusFn = (
  intelligenceType: string | null,
  clip: CentralizedClip | undefined,
  currentWorkspace: WorkspaceListResponse[0] | undefined,
) => {
  if (!intelligenceType || !clip?.id || !clip?.assetId || !currentWorkspace?.id) return null;

  const params = { clipId: clip.id, assetId: clip.assetId, workspaceId: currentWorkspace.id };

  switch (intelligenceType) {
    case 'video':
      return () => Assets.Versions.VideoIntelligence.Status.get(params);
    case 'image':
      return () => Assets.Versions.ImageIntelligence.Status.get(params);
    default:
      return null;
  }
};

const handleIntelligenceStatusChange = (
  currentStatus: IntelligenceStatusType | undefined,
  prevStatusRef: MutableRefObject<IntelligenceStatusType | undefined>,
  clip: CentralizedClip | undefined,
  queryClient: QueryClient,
) => {
  const wasProcessing = prevStatusRef.current && IN_PROGRESS_STATUSES.includes(prevStatusRef.current);
  const isComplete = currentStatus === 'complete';

  if (wasProcessing && isComplete && clip?.id) {
    const clipKey = getClipWithoutWorkspaceIdKey({ clipId: clip.id });
    queryClient.refetchQueries({ queryKey: clipKey });
  }

  prevStatusRef.current = currentStatus;
};

export const usePrivateIntelligence = (clipTypeOverride?: ClipType) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const clip = useSelector(centralizedClipSelector);
  const queryClient = useQueryClient();
  const prevStatusRef = useRef<IntelligenceStatusType | undefined>();
  const isEnterprise = currentWorkspace?.plan === 'enterprise';

  const clipType: ClipType = clipTypeOverride || clip?.type || 'photo';
  const intelligenceType = getIntelligenceType(clipType, clip?.ext || '');

  // Simple addon mapping - only video and image intelligence
  const addonKey: 'videoIntelligence' | 'imageIntelligence' =
    intelligenceType === 'image' ? 'imageIntelligence' : 'videoIntelligence';
  const { canViewAddOn: hasAddon } = useCanViewPrivateAddOn({ key: addonKey });

  // Use the intelligence type directly - no complex fallback logic needed
  const finalIntelligenceType = intelligenceType;
  const finalHasAddon = hasAddon;

  const apiCall = createIntelligenceStatusFn(finalIntelligenceType, clip, currentWorkspace);

  const intelligenceQuery = useQuery({
    queryKey: ['INTELLIGENCE_STATUS', finalIntelligenceType, clip?.id],
    queryFn: apiCall ? () => callAirApi<IntelligenceStatus>(apiCall) : skipToken,
    enabled: !!apiCall,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && typeof data === 'object' && 'status' in data) {
        const status = data.status;
        if (status && COMPLETE_STATUSES.includes(status)) {
          return false;
        }
      }
      return 3000;
    },
    retry: false,
    staleTime: 0,
  });

  const handleStatusChange = useCallback(() => {
    handleIntelligenceStatusChange(intelligenceQuery.data?.status, prevStatusRef, clip, queryClient);
  }, [intelligenceQuery.data?.status, clip, queryClient]);

  useEffect(handleStatusChange, [handleStatusChange]);

  const { data: storage } = useStorage();
  const isOverStorageLimit = storage?.isOutOfStorage || false;

  const summary = useSelector(centralizedClipSummarySelector);
  const keywords = useSelector(centralizedClipKeywordsSelector);
  const isProcessing =
    (intelligenceQuery.data?.status && IN_PROGRESS_STATUSES.includes(intelligenceQuery.data.status)) ||
    intelligenceQuery.isLoading;
  const hasContent = !!(summary || (keywords && keywords.length > 0));
  const hasError = intelligenceQuery.data?.status ? ERROR_STATUSES.includes(intelligenceQuery.data.status) : false;
  const isUnsupported = !finalIntelligenceType;

  return {
    intelligenceType: finalIntelligenceType,
    hasAddon: finalHasAddon,
    isSupported: !!finalIntelligenceType,
    isEnterprise,
    isProcessing,
    hasContent,
    hasError,
    isUnsupported,
    summary,
    keywords,
    showStorageLimit: isOverStorageLimit && !isEnterprise,
    showUpgrade: !finalHasAddon && !isOverStorageLimit && !isEnterprise,
    showSection: hasContent || isProcessing || (finalHasAddon && hasError),
    isLoading: intelligenceQuery.isLoading,
    status: intelligenceQuery.data?.status,
    query: intelligenceQuery,
  };
};
